<template>
  <aside
    class="fixed inset-y-0 left-0 z-50 w-[17rem] bg-white dark:bg-black shadow-xl transform transition-all duration-300 ease-in-out lg:translate-x-0"
    :class="{ '-translate-x-full': !isOpen, 'translate-x-0': isOpen }"
  >
    <div class="flex flex-col h-full">
      <!-- Logo Section -->
      <div
        class="flex items-center justify-center h-16"
      >
        <div class="flex items-center justify-center space-x-2 px-2 w-full">
          <!-- Dynamic Brand Logo -->
          <div class="w-full flex items-center justify-center flex-shrink-0">
            <img
              v-if="brandLogo"
              :src="brandLogo"
              :alt="currentBrand?.name || 'Logo'"
              class="w-full mt-12 object-contain h-24"
            />
            <img
              v-else
              src="@/assets/sample-logo.png"
              alt="Logo"
              class="w-full p-2"
            />
          </div>
          <!-- <h1 class="text-base sm:text-lg lg:text-xl font-bold text-white break-words text-center">
            {{ appTitle }}
          </h1> -->
        </div>
      </div>

      <!-- Navigation Section -->
      <nav
        class="flex-1 mx-4 my-12 px-6 py-4 space-y-5 rounded-xl overflow-y-auto"
        :style="navSectionStyle"
      >
        <router-link
          v-for="item in navigation"
          :key="item.to"
          :to="item.to"
          class="group flex justify-center items-center mt-3 px-3 py-3.5 text-gray-700 rounded-full transition-all duration-200 relative overflow-hidden"
          :class="{
            'text-white': isActive(item.to)
          }"
          :style="isActive(item.to) ? activeNavStyle : inActiveNavStyle"
        >
          <!-- Active Indicator -->
          <!-- <div
            v-if="isActive(item.to)"
            class="absolute left-0 top-0 bottom-0 w-1 rounded-r-full"
            :style="{ backgroundColor: brandColors.accent }"
          ></div> -->

          <!-- Navigation Item Content -->
          <div class="flex justify-center items-center space-x-3 relative z-10 text-center">
            <!-- <div
              class="flex items-center justify-center w-8 h-8 rounded-lg transition-all duration-200"
              :style="isActive(item.to) ? { backgroundColor: 'rgba(255,255,255,0.2)' } : {}"
            >
              <component
                :is="item.icon"
                class="w-5 h-5 flex-shrink-0 transition-all duration-200 group-hover:scale-110"
                :class="isActive(item.to) ? 'text-white' : 'text-gray-600 dark:text-gray-400'"
              />
            </div> -->
            <span class="text-md font-medium truncate">
               {{ item.name === 'What is TCP?' && currentBrand?.name?.toLowerCase() === 'falken' ? 'What is RHR?' : item.name }}
            </span>
          </div>

          <!-- Hover Effect -->
          <div
            v-if="!isActive(item.to)"
            class="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-300"
            :style="hoverStyle"
          ></div>
        </router-link>
      </nav>

      <!-- Footer Section -->
      <div class="p-4 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-black">
        <div class="flex items-center justify-center space-x-2 text-xs text-gray-600 dark:text-gray-400">
          <span>© {{ new Date().getFullYear() }}</span>
          <span class="w-1 h-1 rounded-full bg-gray-400"></span>
          <span>Developed by Proweaver</span>
        </div>
      </div>
    </div>
  </aside>

  <!-- Mobile Overlay -->
  <div
    v-if="isOpen"
    class="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden transition-opacity duration-300"
    @click="toggleSidebar"
  ></div>
</template>

<script setup lang="ts">
import { computed, watch, onMounted } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { getNavigationItems } from '@/router'
import { useBrand } from '@/composables/brand/useBrand'
import { useAuthStore } from '@/stores/auth'

// Props & Emits
const props = defineProps<{
  isOpen: boolean
}>()

const emit = defineEmits<{
  (e: 'update:isOpen', value: boolean): void
}>()

// Composables
const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const { currentBrand, brandLogo, brandColors, applyBrandColors } = useBrand()

// Computed Properties
const appTitle = computed(() => process.env.VUE_APP_TITLE || 'SP Team Template')

// Role-based navigation
const navigation = computed(() => {
  const userRole = authStore.currentUser?.role
  const effectiveRole = (userRole === 'admin' && authStore.isAdminViewMode)
    ? 'customer'
    : userRole
  return getNavigationItems([...router.options.routes], effectiveRole, currentBrand.value?.slug)
})

// Dynamic Styles
const sidebarStyle = computed(() => ({
  backgroundColor: brandColors.value.sidebarBg,
}))

const logoSectionStyle = computed(() => ({
  backgroundColor: brandColors.value.sidebarBg,
  borderBottomColor: 'rgba(255, 255, 255, 0.2)',
}))

const navSectionStyle = computed(() => ({
  backgroundColor: brandColors.value.sidebarBg,
}))

const activeNavStyle = computed(() => ({
  backgroundColor: brandColors.value.sidebarActiveBg,
}))

const inActiveNavStyle = computed(() => ({
  backgroundColor: '#ffffff',
}))

const hoverStyle = computed(() => ({
  backgroundColor: `${brandColors.value.hover}20`,
}))

// Methods
const isActive = (path: string) => route.path === path
const toggleSidebar = () => emit('update:isOpen', !props.isOpen)

// Apply brand colors on mount and when brand changes
onMounted(() => {
  applyBrandColors()
})

watch(() => currentBrand.value, () => {
  applyBrandColors()
}, { immediate: true })
</script>